import { useEffect, useState } from "react";
import {
  AdvanceSearch,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  activeInactiveHelpCategory,
  deleteHelpCategory,
  getAllHelpCategory,
  getHelpCategoryFields,
  IHelpCategory,
} from "./HelpCategoryController";
import { IPagination } from "../../data/AppInterface";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { ORDER_BY } from "../../utils/AppConstants";
import { deleteIcon, editIcon, eyeIcon } from "../../assets/icons/SvgIconList";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  prepareMessageFromParams,
  toggleStatus,
} from "../../utils/AppFunctions";
import HelpCategoryForm from "./HelpCategoryForm";

let timer: ReturnType<typeof setTimeout>;

const HelpCategory = () => {
  const { t } = UseTranslationHook();
  const [helpCategoryList, setHelpCategoryList] = useState<IHelpCategory[]>([]);
  const { pagination, setChangePagination } = UsePaginationHook();
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [helpCategoryToEdit, setHelpCategoryToEdit] = useState<IHelpCategory>();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");

  useEffect(() => {
    fetchHelpCategory({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch]);

  const fetchHelpCategory = (paginationPlayLoad: IPagination) => {
    setIsLoading(true);
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      await getAllHelpCategory(
        setHelpCategoryList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
      );
      setIsLoading(false);
    }, 500);
  };

  const handleHelpCategoryStatus = async (item: IHelpCategory) => {
    const newStatus =
      item.is_active === BIT_VALUE.One ? BIT_VALUE.Zero : BIT_VALUE.One;
    const isUpdated = await activeInactiveHelpCategory(item.id, newStatus);
    if (isUpdated) {
      setHelpCategoryList(toggleStatus(helpCategoryList, item.id));
    }
  };

  const handleDelete = async (value: boolean) => {
    if (value && helpCategoryToEdit) {
      const data = await deleteHelpCategory(helpCategoryToEdit.id);
      if (data) {
        fetchHelpCategory(pagination);
      }
    }
    setShowDeleteConfirmation(false);
    setHelpCategoryToEdit(undefined);
  };

  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setReadOnly(false);
    if (helpCategoryToEdit) {
      setHelpCategoryToEdit(undefined);
    }
    if (status) {
      fetchHelpCategory(pagination);
    }
  };
  const customActionView = (item: IHelpCategory) => {
    return (
      <div className="flex item-center justify-end w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setHelpCategoryToEdit(item);
            setReadOnly(true);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("edit_tooltip_icon")}
          onClick={() => {
            setShowForm(true);
            setHelpCategoryToEdit(item);
          }}
        >
          {editIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("delete_tooltip_icon")}
          onClick={() => {
            setShowDeleteConfirmation(true);
            setHelpCategoryToEdit(item);
          }}
        >
          {deleteIcon}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchHelpCategory(pagination);
        }}
        title={t("help_category")}
      />
      <ICTable<IHelpCategory>
        isLoading={isLoading}
        totalFlex="grid grid-cols-17"
        fields={getHelpCategoryFields(
          customActionView,
          handleHelpCategoryStatus,
          t,
        )}
        data={helpCategoryList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchHelpCategory({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchHelpCategory({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchHelpCategory({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />
      {showForm && (
        <HelpCategoryForm
          handleClose={handleCloseForm}
          helpCategoryToEdit={helpCategoryToEdit}
          readOnly={readOnly}
        />
      )}
      {showDeleteConfirmation && helpCategoryToEdit && (
        <ICConfirmationModal
          title={t("delete_help_category")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", helpCategoryToEdit.category_title],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};

export default HelpCategory;
